import CardMembershipIcon from "@mui/icons-material/CardMembership";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RecordVoiceOverIcon from "@mui/icons-material/RecordVoiceOver";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Toaster } from "react-hot-toast";
import { useLocation, useNavigate } from "react-router-dom";

import { DropdownOption } from "../components";
import SelectAudioDropdown from "../components/select-audio-dropdown.component";
import { _get, _post } from "../services/api.service";
import { _notify, NotificationType } from "../services/notification.service";
import SubscriptionDetailsPage from "./subscription-details.page";

import MainHeading from "../components/main-heading.component";
import { BILLING_ENABLED } from "../config/features";
import { useMainApi } from "../provider/main-api.provider";
import "./video-create/CreateVideo.css";
import "./Settings.css";

const tabs = [
  { label: "Voice", path: "/settings/voice", icon: RecordVoiceOverIcon },
  ...(BILLING_ENABLED
    ? [
        {
          label: "Subscription",
          path: "/settings/subscription",
          icon: CardMembershipIcon,
        },
      ]
    : []),
];

const Settings = () => {
  const [tab, setTab] = useState(0);
  const [voiceId, setVoiceId] = useState<number>(-1);
  const [voices, setVoices] = useState<DropdownOption[]>([]);
  const [audio, setAudio] = useState<HTMLAudioElement | null>(null);

  const location = useLocation();
  const navigate = useNavigate();

  const currentTabIndex = useMemo(
    () => tabs.findIndex((t) => location.pathname.startsWith(t.path)),
    [location.pathname]
  );

  useEffect(() => {
    setTab(currentTabIndex !== -1 ? currentTabIndex : 0);
  }, [currentTabIndex]);

  useEffect(() => {
    if (!BILLING_ENABLED && location.pathname.startsWith("/settings/subscription")) {
      navigate("/settings/voice", { replace: true });
    }
  }, [location.pathname, navigate]);

  const { me, loading, refresh } = useMainApi();

  const selectedVoiceName = useMemo(() => {
    const v = voices.find((v) => v.id === voiceId);
    return v?.label ?? "Not selected";
  }, [voices, voiceId]);

  const selectedVoiceAudioUrl = useMemo(() => {
    const v = voices.find((v) => v.id === voiceId);
    return v?.audio_url ?? v?.url ?? null;
  }, [voices, voiceId]);

  useEffect(() => {
    if (!loading && me) {
      fetchVoices();
    }
  }, [loading, me]);

  const fetchVoices = async () => {
    try {
      const response = await _get("engine/voice/");
      if (!response) return;
      const selectedVoice = me?.profile?.default_voice?.voice_id ?? -1;
      setVoiceId(selectedVoice);
      const formatted = response.map((voice: any) => ({
        ...voice,
        id: voice.voice_id,
        label: voice.name,
        actions: ["listen"],
      }));
      setVoices(formatted);
    } catch (err) {
      console.error("Error fetching voices", err);
    }
  };

  const handleVoiceAction = useCallback(
    (voice: any, actionType: string, isAlreadySelected: boolean) => {
      if (actionType === "select") {
        setVoiceId(voice.id);
      } else if (actionType === "listen") {
        audio?.pause();
        if (!isAlreadySelected) {
          const newAudio = new Audio(voice.audio_url);
          newAudio.play();
          setAudio(newAudio);
        }
      }
    },
    [audio]
  );

  const handleUpdateVoice = async () => {
    try {
      const payload = voices.find((i) => i.voice_id === voiceId);
      const response = await _post("users/update-voice/", payload);
      if (response) {
        _notify(NotificationType.SUCCESS, "Voice is set");
        refresh({ force: true });
      }
    } catch (err) {
      console.error("Failed to update voice", err);
    }
  };

  const handleTabChange = (newValue: number) => {
    setTab(newValue);
    navigate(tabs[newValue].path);
  };

  const handlePreviewVoice = () => {
    if (selectedVoiceAudioUrl) {
      audio?.pause();
      const newAudio = new Audio(selectedVoiceAudioUrl);
      newAudio.play();
      setAudio(newAudio);
    }
  };

  return (
    <div className="container-fluid settings-page">
      <Toaster />
      <MainHeading
        title="Settings"
        subTitle={
          BILLING_ENABLED
            ? "Manage your default voice and subscription."
            : "Manage your default voice."
        }
      />

      <div className="st-layout">
        <nav className="st-tabs" aria-label="Settings sections">
          {tabs.map((t, i) => {
            const Icon = t.icon;
            return (
              <button
                key={t.label}
                type="button"
                className={`st-tab${tab === i ? " is-active" : ""}`}
                onClick={() => handleTabChange(i)}
                aria-current={tab === i ? "page" : undefined}
              >
                <Icon />
                {t.label}
              </button>
            );
          })}
        </nav>

        {tab === 0 && (
          <div className="st-panel" role="tabpanel" id="settings-tabpanel-voice">
            <div className="st-section-head">
              <h3 className="st-section-title">Voice</h3>
              <p className="subtitle">
                Select the default voice used for all your videos.
              </p>
            </div>

            {voiceId > 0 && (
              <div className="st-voice-selected">
                <div className="st-voice-selected-icon">
                  <CheckCircleIcon sx={{ fontSize: 20 }} />
                </div>
                <div className="st-voice-selected-copy">
                  <p className="st-voice-selected-name">{selectedVoiceName}</p>
                  <p className="st-voice-selected-label">Currently selected voice</p>
                </div>
                {selectedVoiceAudioUrl && (
                  <button
                    type="button"
                    className="st-btn-outline"
                    onClick={handlePreviewVoice}
                  >
                    <VolumeUpIcon sx={{ fontSize: 16 }} />
                    Preview
                  </button>
                )}
              </div>
            )}

            <div className="st-voice-picker">
              <SelectAudioDropdown
                label="Select voice"
                options={voices}
                voiceId={voiceId}
                shouldFirstSelected={true}
                onAction={handleVoiceAction}
                showClearSelection={false}
                setOptions={() => {}}
              />
            </div>

            <div className="st-footer">
              <button
                id="settings-update-voice"
                type="button"
                className="cv-btn cv-btn-primary"
                onClick={handleUpdateVoice}
              >
                Save Changes
              </button>
              <p className="st-footnote">
                Explore ultra-realistic AI voices for your videos.
              </p>
            </div>
          </div>
        )}

        {BILLING_ENABLED && tab === 1 && (
          <div className="st-panel" role="tabpanel" id="settings-tabpanel-subscription">
            <SubscriptionDetailsPage />
          </div>
        )}
      </div>
    </div>
  );
};

export default Settings;
